import { useState } from 'react';
import {
  PRIORITIES,
  PRIORITY_LABELS,
  type TaskPatch,
  type TaskPriority,
  type TaskSuggestion,
} from '../../api/organizer';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { formatDayMonthTime } from '../../utils/dateFormat';

interface SuggestionCardProps {
  suggestion: TaskSuggestion;
  onAccept: (edits?: TaskPatch) => void;
  onReject: (reason?: string) => void;
  pending?: boolean;
  /** Set when the last accept/reject for this card failed. */
  error?: string | undefined;
}

function confidenceLabel(confidence: string | null): string | null {
  if (confidence === null) return null;
  const value = Number(confidence);
  if (Number.isNaN(value)) return null;
  if (value >= 0.8) return 'High confidence';
  if (value >= 0.5) return 'Medium confidence';
  return 'Low confidence';
}

function formatMeetingDate(recordedAt: string | null): string | null {
  if (!recordedAt) return null;
  return formatDayMonthTime(recordedAt);
}

/**
 * One proposed to-do, with the evidence for it.
 *
 * The quote is the point of this card: it is what lets a proposal be judged in a
 * second rather than trusted. Accepting is the only path onto the board, so the
 * editable fields are here too — correcting a title while reviewing is much more
 * likely than accepting and then editing on the board.
 */
export function SuggestionCard({
  suggestion,
  onAccept,
  onReject,
  pending,
  error,
}: SuggestionCardProps) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(suggestion.proposedTitle);
  const [dueDate, setDueDate] = useState(suggestion.proposedDueDate ?? '');
  const [priority, setPriority] = useState<TaskPriority>(suggestion.proposedPriority ?? 'medium');

  const evidence = suggestion.evidence;
  const confidence = confidenceLabel(suggestion.confidence);
  const meetingDate = formatMeetingDate(evidence?.recordedAt ?? null);

  return (
    <div className="rounded-xl border border-border-light bg-surface-primary p-4">
      <div className="flex flex-wrap items-center gap-2 text-body-xs text-text-tertiary">
        {evidence?.meetingTitle && (
          <span className="font-medium text-text-secondary">{evidence.meetingTitle}</span>
        )}
        {meetingDate && <span>· {meetingDate}</span>}
        {confidence && (
          <Badge>{confidence}</Badge>
        )}
        {suggestion.origin === 'fathom_action_item' && (
          <Badge>Fathom action item</Badge>
        )}
      </div>

      {editing ? (
        <div className="mt-3 space-y-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-lg border border-border-light bg-surface-primary px-3 py-2 text-body-sm outline-none focus:border-text-primary"
          />
          <div className="flex flex-wrap gap-3">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="rounded-lg border border-border-light bg-surface-primary px-2.5 py-1.5 text-body-xs outline-none focus:border-text-primary"
            />
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority)}
              className="rounded-lg border border-border-light bg-surface-primary px-2.5 py-1.5 text-body-xs outline-none focus:border-text-primary"
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS[p]}
                </option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-body-sm font-medium text-text-primary">{suggestion.proposedTitle}</p>
      )}

      {!editing && suggestion.proposedNotes && (
        <p className="mt-1 text-body-xs text-text-tertiary">{suggestion.proposedNotes}</p>
      )}

      {evidence?.quote && (
        <blockquote className="mt-3 border-l-2 border-border pl-3 text-body-xs italic text-text-tertiary">
          “{evidence.quote}”
          <span className="not-italic">
            {' '}
            — {evidence.speaker}
            {evidence.timestamp && ` at ${evidence.timestamp}`}
          </span>
        </blockquote>
      )}

      {error && (
        <p className="mt-3 text-body-xs text-error" role="alert">
          {error}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          disabled={pending}
          onClick={() =>
            onAccept(
              editing
                ? { title: title.trim() || suggestion.proposedTitle, dueDate: dueDate || null, priority }
                : undefined,
            )
          }
        >
          {editing ? 'Save & accept' : 'Accept'}
        </Button>
        {!editing && (
          <Button size="sm" variant="outline" disabled={pending} onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={pending} onClick={() => onReject()}>
          Reject
        </Button>
        {evidence?.shareUrl && (
          <a
            href={evidence.shareUrl}
            target="_blank"
            rel="noreferrer"
            className="ml-auto text-body-xs font-medium text-accent-dark hover:underline"
          >
            Open the call →
          </a>
        )}
      </div>
    </div>
  );
}
